import type { ReceiveConfirmLineDto } from '@/entities/receive';

import type { ConfirmArrivalLineValues, ConfirmArrivalQuantityField, ConfirmArrivalUnitMode } from '../model/types';
import { CONFIRM_ARRIVAL_UNIT_MODE } from '../model/constants';

/**
 * The part of a line the unit calculator needs. A receipt line has all of it; a
 * releasable line maps onto it — so both modals convert with the same code.
 */
export type BundleConfig = Pick<
  ReceiveConfirmLineDto,
  'bundleQtyAllowed' | 'ltusPerBundle' | 'bundleTypeDescription' | 'ltuTypeDescription'
>;

/** Total the backend will accept across all three condition buckets. */
export const getExpectedTotal = (line: ReceiveConfirmLineDto): number => line.expectedGoodQty + line.expectedDamagedQty;

/**
 * Which fields give up units when another one needs room, in order.
 *
 * Good is always the first donor — it holds the default full expectation, so
 * recording damage or a loss should come out of it before anything else. Only
 * once Good is empty does the remaining bucket pay, which is the "increment
 * Damaged with no Good left → take from Lost, and vice versa" rule.
 */
const DONOR_ORDER: Record<ConfirmArrivalQuantityField, ConfirmArrivalQuantityField[]> = {
  goodQty: ['damagedQty', 'lostQty'],
  damagedQty: ['goodQty', 'lostQty'],
  lostQty: ['goodQty', 'damagedQty']
};

/**
 * The most a single field may hold.
 *
 * Damaged and Lost are bounded only by the line's total expectation, but Good
 * carries the backend's separate `goodQty <= expectedGoodQty` rule — which bites
 * whenever part of the shipment was already expected damaged.
 */
export const getFieldCeiling = (line: ReceiveConfirmLineDto, field: ConfirmArrivalQuantityField): number => {
  const total = getExpectedTotal(line);
  return field === 'goodQty' ? Math.min(line.expectedGoodQty, total) : total;
};

/**
 * Applies one edit to a line and rebalances the rest so the three buckets never
 * sum above the expectation — the redistribution the JSF page did in the
 * browser, restored.
 *
 * Only **overflow** moves. Lowering a field frees capacity and touches nothing
 * else, which is what makes a partial receipt expressible: Good 19 → 8 simply
 * leaves 11 outstanding rather than pushing 11 into another bucket.
 *
 * The edited field is clamped to its own ceiling first, which is what guarantees
 * the donors can absorb the rest: after clamping `next <= total`, so
 * `overflow = next + others - total <= others`.
 */
export const redistributeQuantities = (
  line: ReceiveConfirmLineDto,
  current: ConfirmArrivalLineValues,
  field: ConfirmArrivalQuantityField,
  rawNext: number
): ConfirmArrivalLineValues => {
  const total = getExpectedTotal(line);
  const next = Math.max(0, Math.min(Math.floor(rawNext), getFieldCeiling(line, field)));

  const result: ConfirmArrivalLineValues = { ...current, [field]: next };
  let overflow = result.goodQty + result.damagedQty + result.lostQty - total;

  for (const donor of DONOR_ORDER[field]) {
    if (overflow <= 0) break;
    const taken = Math.min(result[donor], overflow);
    result[donor] -= taken;
    overflow -= taken;
  }

  return result;
};

/** What is still outstanding once the operator's entry is subtracted. */
export const getRemainingQty = (line: ReceiveConfirmLineDto, values: ConfirmArrivalLineValues): number =>
  getExpectedTotal(line) - (values.goodQty + values.damagedQty + values.lostQty);

export const isLineEmpty = (values: ConfirmArrivalLineValues): boolean =>
  values.goodQty === 0 && values.damagedQty === 0 && values.lostQty === 0;

/**
 * Whether the line can be typed in handling units. A missing or non-positive
 * `ltusPerBundle` would make the conversion meaningless, so it disables bundle
 * entry just as `bundleQtyAllowed: false` does.
 */
export const supportsHandlingUnits = (line: BundleConfig): boolean =>
  line.bundleQtyAllowed && Number.isFinite(line.ltusPerBundle) && line.ltusPerBundle > 0;

/**
 * Inventory units → handling units, rounded **up**: a part-filled pallet is
 * still a pallet to handle (legacy `initBundleQuantities`, `Math.ceil`).
 */
export const toHandlingUnits = (line: BundleConfig, inventoryQty: number): number =>
  supportsHandlingUnits(line) ? Math.ceil(inventoryQty / line.ltusPerBundle) : inventoryQty;

/**
 * Handling units → inventory units, rounded **down** to a whole LTU count — the
 * only thing the API accepts — so a conversion never claims an item that is not
 * there. The field ceiling then caps a full pallet at what is expected.
 */
export const toInventoryUnits = (line: BundleConfig, handlingQty: number): number =>
  supportsHandlingUnits(line) ? Math.floor(handlingQty * line.ltusPerBundle) : Math.floor(handlingQty);

/** Converts a displayed value back to the inventory units the form stores. */
export const fromDisplayQty = (line: BundleConfig, mode: ConfirmArrivalUnitMode, value: number): number =>
  mode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING ? toInventoryUnits(line, value) : value;

/** Converts a stored inventory-unit value to what the input shows. */
export const toDisplayQty = (line: BundleConfig, mode: ConfirmArrivalUnitMode, value: number): number =>
  mode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING ? toHandlingUnits(line, value) : value;

/** Unit label shown next to a quantity, per mode. */
export const getUnitLabel = (line: BundleConfig, mode: ConfirmArrivalUnitMode): string =>
  mode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING && supportsHandlingUnits(line)
    ? (line.bundleTypeDescription ?? line.ltuTypeDescription)
    : line.ltuTypeDescription;

/**
 * The legacy dialog's "Handling quantity summary" banner: the total in handling
 * units, per bundle type — summed first, then rounded up once, as legacy does.
 * Lines without a usable bundle configuration contribute nothing, matching JSF,
 * which shows no bundle count when `bundleQtyAllowed` is false.
 */
export const summarizeHandlingUnits = (
  entries: { line: BundleConfig; inventoryQty: number }[]
): { label: string; quantity: number }[] => {
  const totals = new Map<string, number>();

  entries.forEach(({ line, inventoryQty }) => {
    if (!supportsHandlingUnits(line) || inventoryQty <= 0) return;
    const label = line.bundleTypeDescription ?? line.ltuTypeDescription;
    totals.set(label, (totals.get(label) ?? 0) + inventoryQty / line.ltusPerBundle);
  });

  return [...totals.entries()].map(([label, quantity]) => ({ label, quantity: Math.ceil(quantity) }));
};

/** The receipt's summary: every condition bucket of every line counts. */
export const buildHandlingSummary = (lines: ReceiveConfirmLineDto[], values: ConfirmArrivalLineValues[]) => {
  const valuesByLineId = new Map(values.map((line) => [line.lineId, line]));
  return summarizeHandlingUnits(
    lines.flatMap((line) => {
      const entered = valuesByLineId.get(line.lineId);
      return entered ? [{ line, inventoryQty: entered.goodQty + entered.damagedQty + entered.lostQty }] : [];
    })
  );
};
